// src/PublicAgencyPage/mobileView.js
import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import client from "../../../API";
import { useQuery } from "react-query";
import { 
  Typography, 
  Box, 
  Container, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  CircularProgress, 
  Backdrop, 
  Button, 
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Slide,
  Fade,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AppBar,
  Toolbar,
  Fab,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Tab,
  Tabs,
  Badge
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import { 
  GridView, 
  ViewList, 
  Rocket, 
  Star, 
  TrendingUp, 
  Verified, 
  Public,
  QrCode,
  Animation,
  Share,
  Launch,
  Search,
  FilterList,
  Sort,
  LocationOn,
  Email,
  Phone,
  Language,
  Business,
  AutoGraph,
  Groups,
  Visibility,
  Close,
  CloudUpload,
  Menu,
  ExpandMore,
  KeyboardArrowUp,
  FilterAlt,
  Tune,
  Info
} from "@mui/icons-material";

// Social Media Icons
import { FaYoutube, FaInstagram, FaTiktok } from "react-icons/fa";

// Components
import CRMDialog from "../../Misc/crmComponents/crmPopup.js";
import HomeHeader from "../../../Components/HomeHeader.js";
import BookingDialog from "../../Misc/bookingdialog";
import { platformRatesListFromCreator } from "../../../Utils/constants.js";
import { handleAvatarError } from "../../../Utils";
import profilePhoto from "../../../Components/globalAssets/ppfLogo.png";
import { toast } from 'sonner';

/*********************************************************************
 * Mobile-Optimized Animations
 *********************************************************************/
const fadeInMobile = keyframes`
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUpMobile = keyframes`
  from { opacity: 0; transform: translateY(25px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const bounceInMobile = keyframes`
  0% { opacity: 0; transform: scale(0.5) translateY(30px); }
  50% { opacity: 1; transform: scale(1.02) translateY(-5px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const zoomInMobile = keyframes`
  from { opacity: 0; transform: scale(0.7); }
  to { opacity: 1; transform: scale(1); }
`;

const gentleFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
`;

const shimmerMobile = keyframes`
  0% { background-position: -150% 0; }
  100% { background-position: 150% 0; }
`;

/*********************************************************************
 * Mobile-Specific Styled Components
 *********************************************************************/
const MobileAnimatedWrapper = styled('div')(({ theme, animationType, animationsEnabled, delay = 0 }) => {
  if (!animationsEnabled) return {};
  
  const animationMap = {
    'FADE_IN': `${fadeInMobile} 0.6s ease-out ${delay}ms both`,
    'SLIDE_UP': `${slideUpMobile} 0.5s ease-out ${delay}ms both`,
    'BOUNCE_IN': `${bounceInMobile} 0.7s ease-out ${delay}ms both`,
    'ZOOM_IN': `${zoomInMobile} 0.5s ease-out ${delay}ms both`,
  };

  return {
    animation: animationMap[animationType] || animationMap['FADE_IN'],
  };
});

const MobileHeroSection = styled('div')(({ theme, mainColor, accentColor, headerMedia }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  background: headerMedia ? 'transparent' : `linear-gradient(135deg, ${mainColor || '#6F72FF'} 0%, ${accentColor || '#BA88FF'} 100%)`,
  paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(3)})`,
}));

const MobileHeroBackground = styled('div')(({ mediaUrl, isVideo }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  
  ...(isVideo ? {
    '& video': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    }
  } : {
    backgroundImage: `url(${mediaUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }),
  
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%)',
  },
}));

const MobileHeroContent = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  color: '#fff',
  padding: theme.spacing(3, 2),
  maxWidth: '100%',
}));

const MobileCompanyLogo = styled('img')(({ theme, animationsEnabled }) => ({
  height: 60,
  maxWidth: 150,
  objectFit: 'contain',
  borderRadius: '12px',
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.3)}`,
  border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
  marginBottom: theme.spacing(2),
  animation: animationsEnabled ? `${gentleFloat} 6s ease-in-out infinite` : 'none',
  transition: 'all 0.3s ease',
  
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

const MobileTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: 'clamp(1.8rem, 8vw, 2.8rem)',
  background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.9) 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1),
  lineHeight: 1.2,
}));

const MobileSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(0.9rem, 4vw, 1.2rem)',
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 400,
  lineHeight: 1.5,
  marginBottom: theme.spacing(3),
  maxWidth: '90%',
  margin: '0 auto',
}));

const MobileStatsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  margin: theme.spacing(3, 0),
}));

const MobileStatsCard = styled(Paper)(({ theme, animationsEnabled }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(15px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '16px',
  padding: theme.spacing(2),
  textAlign: 'center',
  color: 'white',
  minWidth: 80,
  transition: 'all 0.2s ease',
  animation: animationsEnabled ? `${gentleFloat} 4s ease-in-out infinite` : 'none',
  
  '&:active': {
    transform: 'scale(0.95)',
    background: 'rgba(255, 255, 255, 0.15)',
  },
}));

const MobileContentSection = styled(Box)(({ theme, backgroundColor }) => ({
  padding: theme.spacing(2, 0),
  background: backgroundColor || 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
  minHeight: '50vh',
  paddingBottom: `calc(140px + env(safe-area-inset-bottom, 0px) + ${theme.spacing(3)})`, // Bottom navigation aware
}));

const MobileActionButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: theme.spacing(3),
}));

const MobileActionButton = styled(Button)(({ theme, variant = 'primary' }) => {
  const variants = {
    primary: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
    },
    secondary: {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#fff',
    },
    ghost: {
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      color: '#fff',
    }
  };

  return {
    ...variants[variant],
    borderRadius: '25px',
    padding: '12px 28px',
    fontWeight: 600,
    fontSize: '0.9rem',
    textTransform: 'none',
    minWidth: 140,
    boxShadow: variant !== 'ghost' ? '0 4px 20px rgba(102, 126, 234, 0.3)' : 'none',
    transition: 'all 0.2s ease',
    
    '&:active': {
      transform: 'scale(0.98)',
    },
    
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: variant !== 'ghost' ? '0 6px 25px rgba(102, 126, 234, 0.4)' : '0 4px 15px rgba(0, 0, 0, 0.2)',
    },
  };
});

const MobileCreatorCard = styled(Card)(({ theme, mainColor, accentColor, animationsEnabled }) => ({
  borderRadius: '20px',
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.9)}
  )`,
  backdropFilter: 'blur(15px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  overflow: 'hidden',
  margin: theme.spacing(1, 0),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, ${mainColor || theme.palette.primary.main}, ${accentColor || theme.palette.secondary.main})`,
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s ease',
  },
  
  '&:active': {
    transform: animationsEnabled ? 'scale(0.98)' : 'scale(0.99)',
    boxShadow: '0 2px 15px rgba(0, 0, 0, 0.15)',
  },
  
  '&:hover': {
    transform: animationsEnabled ? 'translateY(-2px)' : 'translateY(-1px)',
    boxShadow: `0 8px 25px ${alpha(mainColor || theme.palette.primary.main, 0.15)}`,
    
    '&::before': {
      transform: 'scaleX(1)',
    },
  },
}));

const MobileFilterBar = styled(Paper)(({ theme, mainColor }) => ({
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(mainColor || theme.palette.primary.main, 0.02)}
  )`,
  backdropFilter: 'blur(15px)',
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  position: 'sticky',
  top: 64, // Below header
  zIndex: 100,
}));

const FloatingFilterButton = styled(Fab)(({ theme, mainColor }) => ({
  position: 'fixed',
  bottom: `calc(140px + env(safe-area-inset-bottom, 0px) + ${theme.spacing(1)})`, // Above bottom nav
  right: theme.spacing(2),
  zIndex: 1000,
  background: `linear-gradient(135deg, ${mainColor || '#667eea'} 0%, ${alpha(mainColor || '#667eea', 0.8)} 100%)`,
  color: '#fff',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  
  '&:hover': {
    background: `linear-gradient(135deg, ${alpha(mainColor || '#667eea', 0.9)} 0%, ${alpha(mainColor || '#667eea', 0.7)} 100%)`,
  },
}));

const MobileBottomNavigation = styled(BottomNavigation)(({ theme, mainColor }) => ({
  position: 'fixed',
  bottom: 0,
  width: '100%',
  zIndex: 1200,
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.95)}, 
    ${alpha(theme.palette.background.paper, 0.9)}
  )`,
  backdropFilter: 'blur(20px)',
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(1)})`,
  paddingTop: theme.spacing(1),
  
  '& .MuiBottomNavigationAction-root': {
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      color: mainColor || theme.palette.primary.main,
    },
  },
}));

const MobileDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px 20px 0 0',
    margin: 0,
    position: 'fixed',
    bottom: 0,
    width: '100%',
    maxWidth: '100%',
    maxHeight: `calc(90vh - 140px - env(safe-area-inset-bottom, 0px))`, // Bottom nav aware
    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(1)})`,
  },
}));

const MobileBottomSheet = styled(SwipeableDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    borderTopLeftRadius: theme.spacing(3),
    borderTopRightRadius: theme.spacing(3),
    maxHeight: '75vh',
    overflowY: 'auto',
    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(2)})`,
    paddingTop: theme.spacing(2),
  },
}));

const LoadingScreenMobile = styled(Box)(({ theme, mainColor, accentColor }) => ({
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  background: `linear-gradient(135deg, ${mainColor || '#6F72FF'}, ${accentColor || '#BA88FF'})`,
  color: theme.palette.common.white,
  padding: theme.spacing(3),
}));

/*********************************************************************
 * Constants
 *********************************************************************/
const PLATFORM_ICONS = {
  youtube: FaYoutube,
  instagram: FaInstagram,
  tiktok: FaTiktok,
};

const PLATFORM_COLORS = {
  youtube: "#FF0000",
  instagram: "#E4405F", 
  tiktok: "#000000",
};

/*********************************************************************
 * Main Component
 *********************************************************************/
const EnhancedAgencyPageMobile = () => {
  const theme = useTheme();
  const { manager } = useParams();
  const [agencyDetails, setAgencyDetails] = useState(null);
  const [rosterSettings, setRosterSettings] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [showCRMDialog, setShowCRMDialog] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [promotionData, setPromotionData] = useState([]);
  const [isLoadingCreator, setIsLoadingCreator] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Enhanced filtering state
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sortOption, setSortOption] = useState("reachDesc");
  
  const hasShownMessage = useRef(false);

  // Fetch roster settings
  const { isLoading: isLoadingSettings } = useQuery(
    ["publicRosterSettings", manager],
    () => client.agency.getPublicRosterSettings(manager),
    {
      onSuccess: (res) => {
        setRosterSettings(res);
        console.log('Loaded roster settings:', res);
      },
      onError: (err) => {
        if (err?.response?.status !== 404) {
          console.error("Failed to fetch public roster settings:", err);
        }
        // Set default settings if none found
        setRosterSettings({
          name: `Creator Roster by: ${manager}`,
          description: "A curated list of creators and all their social stats.",
          color_scheme: {
            main: '#6F72FF',
            accent: '#BA88FF',
            backgroundColor: '#F8FAFF'
          },
          animations_enabled: true,
          animation_type: 'FADE_IN',
          view_type: 'SIMPLE',
          show_stats: true,
          show_contact_info: false,
        });
      },
      refetchOnWindowFocus: false,
    }
  );

  const companyName = rosterSettings?.company?.name || manager;
  
  const { data, isLoading: isLoadingCreators } = useQuery(
    ["agencyDetails", companyName],
    () => client.creators.listManager(companyName),
    {
      enabled: !!companyName && !!rosterSettings, // Only run after settings are loaded
      onSuccess: (data) => setAgencyDetails({ ...data }),
      onError: (error) => console.error("Failed to fetch agency details:", error),
      refetchOnWindowFocus: false,
    }
  );

  // Enhanced creator interaction handlers
  const handleInviteClick = async (creatorName) => {
    setIsLoadingCreator(true);
    try {
      const creatorData = await client.creators.fetchDetails(creatorName);
      setSelectedCreator(creatorData.creator);
      setPromotionData(platformRatesListFromCreator(creatorData.creator));
      setBookingDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch creator details:", error);
      toast.error("Failed to fetch creator details. Please try again.");
    } finally {
      setIsLoadingCreator(false);
    }
  };

  const handleBookingSubmission = async (bookingDetails) => {
    try {
      const result = await client.partnerships.book({
        name: `${bookingDetails.partnershipName} - ${bookingDetails.selectedPromotion}`,
        creator: bookingDetails.creatorId,
        description: bookingDetails.details,
        email: bookingDetails.email,
        proposal_date: bookingDetails.selectedDate,
        total_sum: bookingDetails.rate,
        unique_code: `promo_${bookingDetails.creatorId}_${new Date().getTime()}`,
        notes: bookingDetails.details,
        finish_date: bookingDetails.selectedDate,
      });
      toast.success('Booking successful', {
        description: `Agency: ${result?.agency_name || 'Unknown'}`
      });
      setBookingDialogOpen(false);
      return true;
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error(`Error submitting booking: ${error?.response?.data?.error || error?.message}`);
      return false;
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: rosterSettings?.name || `${manager} Creator Roster`,
          text: rosterSettings?.description || `Check out ${manager}'s creator roster`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
        navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleCloseCRMDialog = () => setShowCRMDialog(false);

  // Enhanced creator filtering and sorting
  const calculateTotalReach = (creator) => {
    return [
      parseInt(creator.youtube) || 0,
      parseInt(creator.instagram) || 0,
      parseInt(creator.tiktok) || 0,
    ].reduce((acc, val) => acc + val, 0);
  };

  const getFilteredSortedCreators = () => {
    if (!agencyDetails?.creators) return [];
    
    let filtered = [...agencyDetails.creators];

    if (searchQuery.trim() !== "") {
      filtered = filtered.filter((c) =>
        c.creator.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (platformFilter !== "all") {
      filtered = filtered.filter((c) => {
        switch (platformFilter) {
          case "youtube":
            return parseInt(c.youtube) > 0;
          case "instagram":
            return parseInt(c.instagram) > 0;
          case "tiktok":
            return parseInt(c.tiktok) > 0;
          default:
            return true;
        }
      });
    }

    return filtered.sort((a, b) => {
      const totalA = calculateTotalReach(a);
      const totalB = calculateTotalReach(b);
      switch (sortOption) {
        case "nameAsc":
          return a.creator.localeCompare(b.creator);
        case "nameDesc":
          return b.creator.localeCompare(a.creator);
        case "reachAsc":
          return totalA - totalB;
        case "reachDesc":
          return totalB - totalA;
        default:
          return 0;
      }
    });
  };

  const filteredCreators = getFilteredSortedCreators();

  // Animation helper
  const getAnimationDelay = (index) => rosterSettings?.animations_enabled ? index * 100 : 0;

  // Tab content helper
  const getCreatorsByTab = () => {
    const creators = filteredCreators;
    switch (currentTab) {
      case 0: return creators; // All
      case 1: return creators.filter(c => parseInt(c.youtube) > 0); // YouTube
      case 2: return creators.filter(c => parseInt(c.instagram) > 0); // Instagram
      case 3: return creators.filter(c => parseInt(c.tiktok) > 0); // TikTok
      default: return creators;
    }
  };

  // Enhanced mobile creator card rendering
  const renderMobileCreatorCard = (creator, index) => {
    const totalReach = calculateTotalReach(creator);
    const delay = getAnimationDelay(index);

    return (
      <MobileAnimatedWrapper
        key={creator.creator || index}
        animationType={rosterSettings?.animation_type}
        animationsEnabled={rosterSettings?.animations_enabled}
        delay={delay}
      >
        <MobileCreatorCard
          mainColor={rosterSettings?.color_scheme?.main}
          accentColor={rosterSettings?.color_scheme?.accent}
          animationsEnabled={rosterSettings?.animations_enabled}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                src={creator.pfphref || profilePhoto}
                alt={creator.creator}
                onError={handleAvatarError(profilePhoto)}
                sx={{
                  width: 60,
                  height: 60,
                  boxShadow: `0 8px 20px ${alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.3)}`,
                  border: `3px solid ${alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.2)}`,
                }}
              />
              
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={700} noWrap sx={{
                  background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${rosterSettings?.color_scheme?.main || theme.palette.primary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  @{creator.display_name || creator.creator}
                </Typography>

                {rosterSettings?.show_stats && (
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <TrendingUp fontSize="small" sx={{ color: rosterSettings?.color_scheme?.accent }} />
                    {totalReach.toLocaleString()} reach
                  </Typography>
                )}

                {rosterSettings?.show_contact_info && creator.email && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Email sx={{ fontSize: 12 }} />
                    {creator.email}
                  </Typography>
                )}
              </Box>

              <Chip
                label="Active"
                size="small"
                sx={{
                  bgcolor: alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.1),
                  color: rosterSettings?.color_scheme?.main || theme.palette.primary.main,
                  fontWeight: 600,
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {creator.youtube && (
                <Chip
                  component={creator.youtube_link ? "a" : "div"}
                  href={creator.youtube_link}
                  target={creator.youtube_link ? "_blank" : undefined}
                  clickable={!!creator.youtube_link}
                  avatar={<Avatar sx={{ bgcolor: 'transparent !important' }}><FaYoutube /></Avatar>}
                  label={`${parseInt(creator.youtube).toLocaleString()}`}
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: alpha(PLATFORM_COLORS.youtube, 0.1),
                    color: PLATFORM_COLORS.youtube,
                    border: `1px solid ${alpha(PLATFORM_COLORS.youtube, 0.2)}`,
                    fontSize: '0.7rem',
                    height: 24,
                  }}
                />
              )}
              {creator.instagram && (
                <Chip
                  component={creator.instagram_link ? "a" : "div"}
                  href={creator.instagram_link}
                  target={creator.instagram_link ? "_blank" : undefined}
                  clickable={!!creator.instagram_link}
                  avatar={<Avatar sx={{ bgcolor: 'transparent !important' }}><FaInstagram /></Avatar>}
                  label={`${parseInt(creator.instagram).toLocaleString()}`}
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: alpha(PLATFORM_COLORS.instagram, 0.1),
                    color: PLATFORM_COLORS.instagram,
                    border: `1px solid ${alpha(PLATFORM_COLORS.instagram, 0.2)}`,
                    fontSize: '0.7rem',
                    height: 24,
                  }}
                />
              )}
              {creator.tiktok && (
                <Chip
                  component={creator.tiktok_link ? "a" : "div"}
                  href={creator.tiktok_link}
                  target={creator.tiktok_link ? "_blank" : undefined}
                  clickable={!!creator.tiktok_link}
                  avatar={<Avatar sx={{ bgcolor: 'transparent !important' }}><FaTiktok /></Avatar>}
                  label={`${parseInt(creator.tiktok).toLocaleString()}`}
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: alpha(PLATFORM_COLORS.tiktok, 0.1),
                    color: PLATFORM_COLORS.tiktok,
                    border: `1px solid ${alpha(PLATFORM_COLORS.tiktok, 0.2)}`,
                    fontSize: '0.7rem',
                    height: 24,
                  }}
                />
              )}
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                href={`/creators/${creator.creator}`}
                target="_blank"
                startIcon={<Launch />}
                sx={{ 
                  borderRadius: '12px',
                  borderColor: rosterSettings?.color_scheme?.accent,
                  color: rosterSettings?.color_scheme?.accent,
                  fontSize: '0.8rem',
                  flex: 1
                }}
              >
                View
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleInviteClick(creator.creator)}
                sx={{ 
                  borderRadius: '12px',
                  bgcolor: rosterSettings?.color_scheme?.main,
                  fontSize: '0.8rem',
                  flex: 1,
                  '&:hover': {
                    bgcolor: alpha(rosterSettings?.color_scheme?.main || theme.palette.primary.main, 0.8),
                  }
                }}
              >
                Collaborate
              </Button>
            </Stack>
          </CardContent>
        </MobileCreatorCard>
      </MobileAnimatedWrapper>
    );
  };

  if (isLoadingCreators || !agencyDetails || isLoadingSettings) {
    return (
      <LoadingScreenMobile
        mainColor={rosterSettings?.color_scheme?.main}
        accentColor={rosterSettings?.color_scheme?.accent}
      >
        <MobileAnimatedWrapper
          animationType="FADE_IN"
          animationsEnabled={true}
          delay={0}
        >
          <CircularProgress 
            size={60} 
            thickness={4} 
            sx={{ mb: 3, color: 'common.white' }} 
          />
        </MobileAnimatedWrapper>
        <MobileAnimatedWrapper
          animationType="SLIDE_UP"
          animationsEnabled={true}
          delay={200}
        >
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1, textAlign: 'center' }}>
            Loading Creator Roster
          </Typography>
        </MobileAnimatedWrapper>
        <MobileAnimatedWrapper
          animationType="SLIDE_UP"
          animationsEnabled={true}
          delay={400}
        >
          <Typography variant="body1" sx={{ opacity: 0.9, textAlign: 'center' }}>
            Preparing amazing creators...
          </Typography>
        </MobileAnimatedWrapper>
      </LoadingScreenMobile>
    );
  }

  const mainColor = rosterSettings?.color_scheme?.main || "#6F72FF";
  const accentColor = rosterSettings?.color_scheme?.accent || "#BA88FF";
  const backgroundColor = rosterSettings?.color_scheme?.backgroundColor || "#F8FAFF";
  const rosterName = rosterSettings?.name || `Creator Roster by: ${manager}`;
  const rosterDescription = rosterSettings?.description || "A curated list of creators and all their social stats.";
  const rosterLogo = rosterSettings?.logo;
  const headerMedia = rosterSettings?.header_media_url;
  const isHeaderVideo = headerMedia && /\.(mp4|webm|ogg)$/i.test(headerMedia);
  
  const totalCreators = agencyDetails?.creators?.length || 0;
  const totalReach = agencyDetails?.creators?.reduce((sum, creator) => {
    return sum + calculateTotalReach(creator);
  }, 0) || 0;

  const currentCreators = getCreatorsByTab();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: backgroundColor }}>
      <HomeHeader variant="minimal" />

      {/* Mobile Hero Section */}
      <MobileHeroSection 
        mainColor={mainColor} 
        accentColor={accentColor}
        headerMedia={!!headerMedia}
      >
        {headerMedia && (
          <MobileHeroBackground mediaUrl={headerMedia} isVideo={isHeaderVideo}>
            {isHeaderVideo && (
              <video autoPlay muted loop>
                <source src={headerMedia} type="video/mp4" />
              </video>
            )}
          </MobileHeroBackground>
        )}

        <MobileHeroContent>
          <MobileAnimatedWrapper
            animationType={rosterSettings?.animation_type}
            animationsEnabled={rosterSettings?.animations_enabled}
            delay={100}
          >
            <Box>
              {rosterLogo && (
                <MobileCompanyLogo
                  src={rosterLogo}
                  alt="Agency Logo"
                  animationsEnabled={rosterSettings?.animations_enabled}
                />
              )}
              
              <MobileTitle variant="h4">
                {rosterName}
              </MobileTitle>
              
              <MobileSubtitle variant="body1">
                {rosterDescription}
              </MobileSubtitle>
            </Box>
          </MobileAnimatedWrapper>

          <MobileAnimatedWrapper
            animationType={rosterSettings?.animation_type}
            animationsEnabled={rosterSettings?.animations_enabled}
            delay={200}
          >
            <MobileStatsGrid>
              {[
                { label: 'Creators', value: totalCreators, icon: <Groups fontSize="small" /> },
                { label: 'Total Reach', value: `${(totalReach / 1000000).toFixed(1)}M`, icon: <TrendingUp fontSize="small" /> },
                { label: 'Active', value: totalCreators, icon: <Verified fontSize="small" /> },
              ].map((stat, index) => (
                <MobileStatsCard key={stat.label} animationsEnabled={rosterSettings?.animations_enabled}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                    {stat.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                    {stat.label}
                  </Typography>
                </MobileStatsCard>
              ))}
            </MobileStatsGrid>
          </MobileAnimatedWrapper>

          <MobileAnimatedWrapper
            animationType={rosterSettings?.animation_type}
            animationsEnabled={rosterSettings?.animations_enabled}
            delay={300}
          >
            <MobileActionButtonGroup>
              <Stack direction="row" spacing={2}>
                <MobileActionButton
                  variant="ghost"
                  startIcon={<Share />}
                  onClick={handleShare}
                  size="small"
                >
                  Share
                </MobileActionButton>
                
                <MobileActionButton
                  variant="ghost"
                  startIcon={<Info />}
                  onClick={() => setShowInstructions(true)}
                  size="small"
                >
                  Tour
                </MobileActionButton>
              </Stack>
            </MobileActionButtonGroup>
          </MobileAnimatedWrapper>
        </MobileHeroContent>
      </MobileHeroSection>

      {/* Mobile Content Section */}
      <MobileContentSection backgroundColor={backgroundColor}>
        <Container maxWidth="sm" sx={{ px: 2 }}>
          {/* Simplified Filter Bar */}
          <MobileAnimatedWrapper
            animationType={rosterSettings?.animation_type}
            animationsEnabled={rosterSettings?.animations_enabled}
            delay={400}
          >
            <MobileFilterBar mainColor={mainColor}>
              <TextField
                placeholder="Search creators..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: '100%',
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip 
                  label={`${currentCreators.length} creators`}
                  sx={{
                    backgroundColor: alpha(mainColor, 0.1),
                    color: mainColor,
                    fontWeight: 600,
                    borderRadius: '12px',
                  }}
                />
                
                <IconButton 
                  onClick={() => setFiltersOpen(true)}
                  sx={{ 
                    bgcolor: alpha(mainColor, 0.1),
                    color: mainColor,
                    '&:hover': { bgcolor: alpha(mainColor, 0.2) }
                  }}
                >
                  <Badge 
                    badgeContent={platformFilter !== 'all' || sortOption !== 'reachDesc' ? 1 : 0}
                    color="error"
                  >
                    <Tune />
                  </Badge>
                </IconButton>
              </Box>
            </MobileFilterBar>
          </MobileAnimatedWrapper>
          
          {/* Platform Tabs */}
          <MobileAnimatedWrapper
            animationType={rosterSettings?.animation_type}
            animationsEnabled={rosterSettings?.animations_enabled}
            delay={500}
          >
            <Paper sx={{ borderRadius: '16px', mb: 3, overflow: 'hidden' }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 80,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '0.85rem',
                  },
                  '& .Mui-selected': {
                    color: mainColor,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: mainColor,
                  },
                }}
              >
                <Tab label="All" />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FaYoutube size={14} color="#FF0000" />
                      YouTube
                    </Box>
                  }
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FaInstagram size={14} color="#E4405F" />
                      Instagram
                    </Box>
                  }
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FaTiktok size={14} />
                      TikTok
                    </Box>
                  }
                />
              </Tabs>
            </Paper>
          </MobileAnimatedWrapper>

          {/* Creators List */}
          {currentCreators.length === 0 ? (
            <MobileAnimatedWrapper
              animationType={rosterSettings?.animation_type}
              animationsEnabled={rosterSettings?.animations_enabled}
              delay={600}
            >
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
                  No creators found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Try adjusting your search or filters
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSearchQuery('');
                    setPlatformFilter('all');
                    setCurrentTab(0);
                  }}
                  sx={{
                    borderRadius: '12px',
                    bgcolor: mainColor,
                    '&:hover': { bgcolor: alpha(mainColor, 0.8) }
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </MobileAnimatedWrapper>
          ) : (
            <Box>
              {currentCreators.map((creator, index) => renderMobileCreatorCard(creator, index))}
            </Box>
          )}

          {/* Mobile Watermark */}
          <MobileAnimatedWrapper
            animationType={rosterSettings?.animation_type}
            animationsEnabled={rosterSettings?.animations_enabled}
            delay={800}
          >
            <Box sx={{ textAlign: 'center', mt: 4, mb: 2, opacity: 0.7 }}>
              <Divider sx={{ mb: 2, background: `linear-gradient(90deg, transparent, ${alpha(mainColor, 0.3)}, transparent)`, height: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: mainColor }}>
                Powered by Blitz ✨
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Creator collaboration platform
              </Typography>
            </Box>
          </MobileAnimatedWrapper>
        </Container>
      </MobileContentSection>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNavigation
        mainColor={mainColor}
        value={currentTab}
        onChange={(event, newValue) => setCurrentTab(newValue)}
        showLabels
      >
        <BottomNavigationAction label="All" icon={<Groups />} />
        <BottomNavigationAction label="YouTube" icon={<FaYoutube />} />
        <BottomNavigationAction label="Instagram" icon={<FaInstagram />} />
        <BottomNavigationAction label="TikTok" icon={<FaTiktok />} />
      </MobileBottomNavigation>

      {/* Floating Filter Button */}
      <FloatingFilterButton
        mainColor={mainColor}
        onClick={() => setFiltersOpen(true)}
      >
        <Badge 
          badgeContent={platformFilter !== 'all' || sortOption !== 'reachDesc' ? 1 : 0}
          color="error"
        >
          <Tune />
        </Badge>
      </FloatingFilterButton>

      {/* Mobile Filter Bottom Sheet */}
      <MobileBottomSheet
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onOpen={() => setFiltersOpen(true)}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={700}>
              Filter & Sort
            </Typography>
            <IconButton onClick={() => setFiltersOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>

          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="all">All Platforms</MenuItem>
                <MenuItem value="youtube">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaYoutube color="#FF0000" />
                    YouTube Only
                  </Box>
                </MenuItem>
                <MenuItem value="instagram">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaInstagram color="#E4405F" />
                    Instagram Only
                  </Box>
                </MenuItem>
                <MenuItem value="tiktok">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaTiktok />
                    TikTok Only
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="nameAsc">Name (A-Z)</MenuItem>
                <MenuItem value="nameDesc">Name (Z-A)</MenuItem>
                <MenuItem value="reachAsc">Reach (Low to High)</MenuItem>
                <MenuItem value="reachDesc">Reach (High to Low)</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setPlatformFilter('all');
                  setSortOption('reachDesc');
                }}
                fullWidth
                sx={{ borderRadius: '12px' }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                onClick={() => setFiltersOpen(false)}
                fullWidth
                sx={{ 
                  borderRadius: '12px',
                  bgcolor: mainColor,
                  '&:hover': { bgcolor: alpha(mainColor, 0.8) }
                }}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </MobileBottomSheet>

      {/* Dialogs */}
      <BookingDialog
        open={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        submitBooking={handleBookingSubmission}
        creator={selectedCreator}
        promotionData={promotionData}
      />


      <MobileDialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        fullWidth
      >
        <AppBar sx={{ position: 'relative', background: `linear-gradient(135deg, ${mainColor} 0%, ${accentColor} 100%)` }}>
          <Toolbar>
            <Typography sx={{ flex: 1 }} variant="h6">
              Creator Roster Guide
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setShowInstructions(false)}>
              <Close />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            Welcome to our creator roster! Here's how to get the most out of your experience:
          </Typography>
          
          <Stack spacing={2}>
            {[
              {
                icon: <Search />,
                title: 'Search & Filter',
                desc: 'Use the search bar and platform tabs to find specific creators'
              },
              {
                icon: <Tune />,
                title: 'Advanced Filters',
                desc: 'Tap the filter button to sort by reach, name, or platform'
              },
              {
                icon: <Launch />,
                title: 'View Profiles',
                desc: 'Tap "View" to see detailed creator media kits and stats'
              },
              {
                icon: <Business />,
                title: 'Start Collaborating',
                desc: 'Use "Collaborate" to begin partnership discussions'
              }
            ].map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, p: 1 }}>
                <Box sx={{ color: mainColor, mt: 0.5 }}>
                  {feature.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setShowInstructions(false)}
            variant="contained"
            fullWidth
            sx={{ 
              borderRadius: '12px',
              bgcolor: mainColor,
              '&:hover': { bgcolor: alpha(mainColor, 0.8) }
            }}
          >
            Start Exploring!
          </Button>
        </DialogActions>
      </MobileDialog>

      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(4px)'
        }}
        open={isLoadingCreator}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={50} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading creator details...
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default EnhancedAgencyPageMobile;